(function () {
  'use strict';

  const namespace = window.AVAL = window.AVAL || {};
  if (namespace.booking) return;

  function safeJSONParse(value, fallback) {
    try {
      return JSON.parse(String(value || ''));
    } catch (error) {
      return fallback === undefined ? null : fallback;
    }
  }

  function normalizeText(value) {
    return String(value ?? '').trim().replace(/\s+/g, ' ');
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function toNumber(value) {
    const number = Number(String(value ?? '').replace(',', '.'));
    return Number.isFinite(number) ? number : NaN;
  }

  function round2(value) {
    const number = toNumber(value);
    return Number.isFinite(number) ? Math.round(number * 100) / 100 : 0;
  }

  function todayISO() {
    const date = new Date();
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().slice(0, 10);
  }


  function formatDateDE(value) {
    if (!value) return '–';

    const parts = String(value).split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;

    return String(value);
  }

  function formatArt(value, fallback) {
    if (!value) return fallback === undefined ? 'Packstück' : String(fallback);

    const text = String(value);
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  function formatEUR(value) {
    const number = toNumber(value);
    if (!Number.isFinite(number)) return '';
    return (Math.round(number * 100) / 100).toFixed(2).replace('.', ',');
  }

  function formatTimeWindowHtml(value, fixTime) {
    const text = String(value || '').trim();

    if (!text) return '';
    if (text === 'FIX') return `Fixtermin${fixTime ? ` (${escapeHtml(fixTime)} Uhr)` : ''}`;
    if (/Uhr/i.test(text)) return escapeHtml(text);

    const match = text.match(/^(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})$/);
    if (match) return `${match[1]} Uhr - ${match[2]} Uhr`;

    return escapeHtml(text);
  }

  function parseHttpUrl(value) {
    const text = String(value || '').trim();
    if (!text || text.length > 2048) return null;

    try {
      const url = new URL(text);
      const hostname = String(url.hostname || '').toLowerCase();
      const isLocalDevelopment = (
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname === '[::1]'
      );

      if (url.protocol !== 'https:' && !(isLocalDevelopment && url.protocol === 'http:')) return null;
      if (url.username || url.password || url.hash) return null;

      return url;
    } catch (error) {
      return null;
    }
  }

  function workerBase(value) {
    const url = parseHttpUrl(value);
    if (!url) return '';

    url.search = '';
    url.hash = '';

    return url.toString().replace(/\/+$/, '');
  }

  function validHttpUrl(value) {
    return parseHttpUrl(value) !== null;
  }

  function buildResumeUrl(baseUrl, origin) {
    const resolvedOrigin = String(origin || window.location.origin);
    const backUrl = String(baseUrl || '');

    try {
      const url = new URL(backUrl, resolvedOrigin);
      url.searchParams.set('resume', '1');

      if (url.origin === resolvedOrigin) {
        return url.pathname + url.search + url.hash;
      }

      return url.toString();
    } catch (error) {
      return backUrl.indexOf('?') === -1 ? backUrl + '?resume=1' : backUrl + '&resume=1';
    }
  }

  function step2Signature(step2) {
    if (!step2) return '';

    return JSON.stringify({
      booking_id: step2.booking_id || '',
      step1_price_signature: step2.step1_price_signature || '',
      packstuecke: Array.isArray(step2.packstuecke) ? step2.packstuecke : [],
      optionen: step2.optionen || {},
      preis_basis: step2.preis_basis || '',
      preis_total: step2.preis_total || '',
      request_mode: step2.request_mode === true,
      step1_availability_required: step2.step1_availability_required === true,
      step1_availability_valid: step2.step1_availability_valid === true,
      step1_availability_signature: step2.step1_availability_signature || step2.availability_snapshot?.signature || '',
      availability_hold_id: step2.availability_hold_id || step2.availability_snapshot?.hold_id || '',
      availability_hold_expires_at: step2.availability_hold_expires_at || step2.availability_snapshot?.hold_expires_at || ''
    });
  }


  function step2PriceStructureSignature(step2, priceRows, fallbackZollSurcharge) {
    if (!step2) return '';

    const rows = (Array.isArray(priceRows) ? priceRows : []).map((row) => ({
      key: normalizeText(row?.key || ''),
      label: normalizeText(row?.label || ''),
      amount: round2(row?.amount).toFixed(2),
      total: row?.total === true
    }));

    const components = step2?.preis_komponenten && typeof step2.preis_komponenten === 'object'
      ? step2.preis_komponenten
      : null;

    const fallbackZoll = Number.isFinite(toNumber(fallbackZollSurcharge))
      ? round2(fallbackZollSurcharge)
      : getZollSurcharge(step2);

    return JSON.stringify({
      booking_id: step2.booking_id || '',
      step1_price_signature: step2.step1_price_signature || '',
      pricing_version: step2.pricing_version || components?.pricing_version || '',
      preis_basis: step2.preis_basis || '',
      preis_total: step2.preis_total || '',
      preis_zeilen: rows,
      price_meta: {
        route_type: components?.route_type || '',
        extra_stop_count: components ? Math.max(0, Math.round(Number(components.extra_stop_count) || 0)) : 0,
        billable_extra_km: components ? round2(components.billable_extra_km).toFixed(2) : '0.00',
        zoll: components ? round2(components.zoll).toFixed(2) : fallbackZoll.toFixed(2),
        net_total: components ? round2(components.net_total).toFixed(2) : round2(step2.preis_total).toFixed(2)
      },
      request_mode: step2.request_mode === true
    });
  }

  function sanitizePriceRows(rows) {
    const items = Array.isArray(rows) ? rows : [];

    return items
      .map((row) => {
        const label = normalizeText(row?.label || '');
        const key = normalizeText(row?.key || '');
        const amount = round2(row?.amount);

        if (!label) return null;

        return {
          key: key || label.toLowerCase().replace(/\s+/g, '_'),
          label,
          amount,
          total: row?.total === true || key === 'net_total'
        };
      })
      .filter(Boolean);
  }

  function extractNetTotalFromRows(rows) {
    const items = Array.isArray(rows) ? rows : [];
    const explicitTotal = items.find((row) => row?.total === true || row?.key === 'net_total');
    if (explicitTotal && round2(explicitTotal.amount) > 0) return round2(explicitTotal.amount);

    if (items.length === 1 && round2(items[0].amount) > 0) return round2(items[0].amount);

    return 0;
  }

  function getZollSurcharge(step2, priceSnapshot) {
    const fromSurcharges = toNumber(step2?.zuschlaege?.zoll);
    if (Number.isFinite(fromSurcharges) && fromSurcharges > 0) return round2(fromSurcharges);

    const fromComponents = toNumber(step2?.preis_komponenten?.zoll);
    if (Number.isFinite(fromComponents) && fromComponents > 0) return round2(fromComponents);

    const fromSnapshot = toNumber(priceSnapshot?.preis_komponenten?.zoll);
    if (Number.isFinite(fromSnapshot) && fromSnapshot > 0) return round2(fromSnapshot);

    return step2?.optionen?.zoll === 'ja' ? 50 : 0;
  }

  function createStorage(options) {
    const persistentFallback = options?.persistentFallback === true;

    function readRaw(key) {
      let sessionValue = null;
      try { sessionValue = sessionStorage.getItem(key); } catch (error) {}
      if (sessionValue !== null && sessionValue !== '') return sessionValue;

      if (persistentFallback) {
        try {
          const localValue = localStorage.getItem(key);
          if (localValue !== null && localValue !== '') return localValue;
        } catch (error) {}
      }

      return null;
    }

    function readJSON(key) {
      const sessionValue = (() => {
        try { return sessionStorage.getItem(key); } catch (error) { return null; }
      })();

      if (sessionValue !== null && sessionValue !== '') {
        const parsed = safeJSONParse(sessionValue);
        if (parsed !== null) return parsed;
      }

      if (persistentFallback) {
        try {
          const localValue = localStorage.getItem(key);
          if (localValue !== null && localValue !== '') {
            const parsed = safeJSONParse(localValue);
            if (parsed !== null) return parsed;
          }
        } catch (error) {}
      }

      return null;
    }

    function readText(key) {
      const value = readRaw(key);
      return value === null ? '' : String(value);
    }

    function remove(key) {
      try { sessionStorage.removeItem(key); } catch (error) {}
      try { localStorage.removeItem(key); } catch (error) {}
    }

    function writeRaw(key, value) {
      const text = String(value ?? '');
      let stored = false;

      try {
        sessionStorage.setItem(key, text);
        stored = sessionStorage.getItem(key) === text;
      } catch (error) {}

      if (stored) {
        try { localStorage.removeItem(key); } catch (error) {}
        return true;
      }

      if (persistentFallback) {
        try {
          localStorage.setItem(key, text);
          stored = localStorage.getItem(key) === text;
        } catch (error) {}
      } else {
        try { localStorage.removeItem(key); } catch (error) {}
      }

      return stored;
    }

    function writeJSON(key, payload) {
      let json;
      try { json = JSON.stringify(payload); } catch (error) { return false; }
      if (json === undefined) return false;
      if (!writeRaw(key, json)) return false;
      return readJSON(key) !== null;
    }

    function writeText(key, value) {
      return writeRaw(key, String(value ?? ''));
    }

    function hasAny(key) {
      try {
        const sessionValue = sessionStorage.getItem(key);
        if (sessionValue !== null && sessionValue !== '') return true;
      } catch (error) {}

      if (persistentFallback) {
        try {
          const localValue = localStorage.getItem(key);
          if (localValue !== null && localValue !== '') return true;
        } catch (error) {}
      }

      return false;
    }

    return Object.freeze({
      readJSON,
      readText,
      writeJSON,
      writeText,
      remove,
      hasAny
    });
  }

  namespace.booking = Object.freeze({
    safeJSONParse,
    normalizeText,
    escapeHtml,
    toNumber,
    round2,
    todayISO,
    formatDateDE,
    formatArt,
    formatEUR,
    formatTimeWindowHtml,
    parseHttpUrl,
    workerBase,
    validHttpUrl,
    buildResumeUrl,
    step2Signature,
    step2PriceStructureSignature,
    sanitizePriceRows,
    extractNetTotalFromRows,
    getZollSurcharge,
    createStorage
  });
})();
